import React from 'react';
import styles from './Button.module.css';
import { clsx } from 'clsx';
import { Icon, IconName } from '../../foundations';
import { ForwardRefComponent } from '../../../helpers/polymorfic-components';

const defaultButtonElement: React.ElementType = 'button';

export type ButtonProps = {
	/**
	 * Name of the icon displayed inside the button.
	 */
	icon?: IconName;
	/**
	 * Indicates button variant
	 * @default primary
	 */
	variant?: 'primary' | 'secondary' | 'text' | 'black' | 'gray';
	/**
	 * Indicates whether the button has border
	 * @default false
	 */
	bordered?: boolean;
	/**
	 * Indicates button size
	 * @default big
	 */
	buttonSize?: 'small' | 'big';

	/**
	 * Indicates whether the button has a width of 100%
	 * @default false
	 */
	full?: boolean;
	as?: 'button' | 'p' | 'span' | 'a';
	/**
	 * If the button has to be shown disabled
	 * @default false
	 */
	disabled?: boolean;
	children?: React.ReactNode;
	/**
	 * Styles to apply to the component
	 */
	className?: string;
	/**
	 * Indicate the type of the button.
	 * @default button
	 */
	type?: 'button' | 'submit' | 'reset';
	testId?: string;
	onClick?: () => void;
};

type PolymorphicButton = ForwardRefComponent<typeof defaultButtonElement, ButtonProps>;

export const Button = React.forwardRef<
	HTMLButtonElement,
	ButtonProps & React.ComponentPropsWithoutRef<'button'> & { as?: React.ElementType }
>(
	(
		{
			children,
			as = 'button',
			disabled = false,
			buttonSize = 'big',
			variant = 'primary',
			bordered = false,
			full = false,
			icon,
			type = 'button',
			className,
			testId = 'button',
			...props
		},
		ref
	) => {
		const Component = as as 'button';

		return (
			<Component
				ref={ref}
				className={clsx(
					styles.button,
					styles[buttonSize],
					styles[variant],
					full && styles.full,
					bordered && styles.bordered,
					className
				)}
				disabled={disabled}
				type={type}
				data-testid={testId}
				{...props}
			>
				{icon && <Icon className={styles.buttonIcon} name={icon} />}
				{children}
			</Component>
		);
	}
) as PolymorphicButton;
Button.displayName = 'ButtonDigital';
